import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { WorkflowEstateRule } from '@/modules/workflow/models/WorkflowEstateRule';
import { WorkflowJurisdictionRule } from '@/modules/workflow/models/WorkflowJurisdictionRule';
import { WorkflowJurisdictionRuleCollection } from '@/modules/workflow/models/WorkflowJurisdictionRuleCollection';
import { WorkflowEstateRuleCollection } from '@/modules/workflow/models/WorkflowEstateRuleCollection';
import { WorkflowAssigneeCollection } from '@/modules/workflow/models/WorkflowAssigneeCollection';
import { WorkflowEstateStatusTrigger } from '@/modules/workflow/models/WorkflowEstateStatusTrigger';
import { WorkflowStepCompletedTrigger } from '@/modules/workflow/models/WorkflowStepCompletedTrigger';
import { WorkflowCreateEstateTaskAction } from '@/modules/workflow/models/WorkflowCreateEstateTaskAction';
import { WorkflowTrigger } from '@/modules/workflow/models/WorkflowTrigger';
import { BaseWorkflowRuleCollection } from '@/modules/workflow/models/BaseWorkflowRuleCollection';
import Lang from '@/modules/core/app/services/Lang';
import { WorkflowRuleSelect } from '/@/modules/workflow/models/WorkflowRuleSelect';
import { Jurisdiction } from '@/modules/predefined/models/Jurisdiction';
import { WorkflowRuleType } from '@/modules/workflow/enums/WorkflowRuleType';
import { EstateType } from '@/modules/estate/enums/EstateType';
import { WorkflowTriggerType } from '@/modules/workflow/enums/WorkflowTriggerType';
import { WorkflowBillingPlanRuleCollection } from '@/modules/workflow/models/WorkflowBillingPlanRuleCollection';
import { BillingPlanMapping } from '@/modules/billing/models/BillingPlanMapping';
import { WorkflowActionType } from '@/modules/workflow/enums/WorkflowActionType';
import { WorkflowBillingPlanRule } from '@/modules/workflow/models/WorkflowBillingPlanRule';
import { WorkflowPaymentStatusRuleCollection } from '@/modules/workflow/models/WorkflowPaymentStatusRuleCollection';
import { EstatePaymentStatus } from '@/modules/estate/constants/EstatePaymentStatus';
import { WorkflowTeamRule } from '@/modules/workflow/models/WorkflowTeamRule';
import { WorkflowNotaryStatusTrigger } from '@/modules/workflow/models/WorkflowNotaryStatusTrigger';

export class Workflow extends BaseEntityModel {
  id?: number;

  name?: string;

  type?: string;

  triggerType?: string;

  actionType?: string;

  isEnabled?: boolean;

  workflowEstateRules: Relationship<WorkflowEstateRuleCollection>;

  workflowJurisdictionRules: Relationship<WorkflowJurisdictionRuleCollection>;

  workflowBillingPlanRules: Relationship<WorkflowBillingPlanRuleCollection>;

  workflowPaymentStatusRules: Relationship<WorkflowPaymentStatusRuleCollection>;

  workflowAssignees: Relationship<WorkflowAssigneeCollection>;

  workflowEstateStatusTrigger: Relationship<WorkflowEstateStatusTrigger | null>;

  workflowNotaryStatusTrigger: Relationship<WorkflowNotaryStatusTrigger | null>;

  workflowStepCompletedTrigger: Relationship<WorkflowStepCompletedTrigger | null>;

  workflowCreateEstateTaskAction: Relationship<WorkflowCreateEstateTaskAction | null>;

  workflowTeamRule: Relationship<WorkflowTeamRule | null>;

  constructor(model: Partial<Workflow> = {}) {
    super(model);

    this.id = model.id;
    this.name = model.name;
    this.type = model.type;
    this.triggerType = model.triggerType;
    this.actionType = model.actionType;
    this.isEnabled = model.isEnabled;
    this.workflowEstateRules = WorkflowEstateRuleCollection.mapModel(model.workflowEstateRules);
    this.workflowJurisdictionRules = WorkflowJurisdictionRuleCollection.mapModel(
      model.workflowJurisdictionRules
    );
    this.workflowBillingPlanRules = WorkflowBillingPlanRuleCollection.mapModel(
      model.workflowBillingPlanRules
    );
    this.workflowPaymentStatusRules = WorkflowPaymentStatusRuleCollection.mapModel(
      model.workflowPaymentStatusRules
    );
    this.workflowAssignees = WorkflowAssigneeCollection.mapModel(model.workflowAssignees);
    this.workflowEstateStatusTrigger = WorkflowEstateStatusTrigger.mapModelOrNull(
      model.workflowEstateStatusTrigger
    );
    this.workflowNotaryStatusTrigger = WorkflowNotaryStatusTrigger.mapModelOrNull(
      model.workflowNotaryStatusTrigger
    );
    this.workflowStepCompletedTrigger = WorkflowStepCompletedTrigger.mapModelOrNull(
      model.workflowStepCompletedTrigger
    );
    this.workflowCreateEstateTaskAction = WorkflowCreateEstateTaskAction.mapModelOrNull(
      model.workflowCreateEstateTaskAction
    );
    this.workflowTeamRule = WorkflowTeamRule.mapModelOrNull(model.workflowTeamRule);
  }

  get workflowTrigger(): WorkflowTrigger | null {
    switch (this.triggerType) {
      case WorkflowTriggerType.ESTATE_CREATED.key:
        return WorkflowTrigger.mapModel({
          type: this.triggerType,
          triggerText: Lang.t('workflow.estate-created')
        });
      case WorkflowTriggerType.ESTATE_STATUS_CHANGED.key:
        return WorkflowTrigger.mapModel({
          type: this.triggerType,
          trigger: this.workflowEstateStatusTrigger?.status,
          triggerText: Lang.t('workflow.estate-status-changes'),
          statusText: this.workflowEstateStatusTrigger?.statusText,
          statusColor: this.workflowEstateStatusTrigger?.statusColor
        });
      case WorkflowTriggerType.ESTATE_HEALTH_CHECK_RECOMMENDATION_STATUS_CHANGED.key:
        return WorkflowTrigger.mapModel({
          type: this.triggerType,
          trigger: this.workflowEstateStatusTrigger?.status,
          triggerText: Lang.t('workflow.estate-health-check-recommendation-status-changes'),
          statusText: this.workflowEstateStatusTrigger?.statusText,
          statusColor: this.workflowEstateStatusTrigger?.statusColor
        });
      case WorkflowTriggerType.GUIDANCE_STEP_COMPLETED.key:
        return WorkflowTrigger.mapModel({
          type: this.triggerType,
          trigger: this.workflowStepCompletedTrigger?.key,
          triggerText: Lang.t('workflow.step-completed'),
          statusText: this.workflowStepCompletedTrigger?.stepName
        });
      case WorkflowTriggerType.PRIMARY_ESTATE_PROFESSIONAL_ASSIGNED.key:
        return WorkflowTrigger.mapModel({
          type: this.triggerType,
          triggerText: Lang.t('workflow.primary-ep-is-assinged')
        });
      case WorkflowTriggerType.NOTARY_STATUS_CHANGED.key: {
        return WorkflowTrigger.mapModel({
          type: this.triggerType,
          trigger: this.workflowNotaryStatusTrigger?.status,
          triggerText: Lang.t('workflow.notary-status-changes'),
          statusText: this.workflowNotaryStatusTrigger?.statusText,
          statusColor: this.workflowNotaryStatusTrigger?.statusColor
        });
      }
      default:
        return null;
    }
  }

  get estateTaskWorkflowHasRequiredFields(): boolean {
    const hasAssignee: boolean =
      !!this.workflowCreateEstateTaskAction?.assigneeType || !!this.workflowTeamRule;

    return !!this.name && hasAssignee;
  }

  get workflowRuleCollections(): BaseWorkflowRuleCollection<
    WorkflowJurisdictionRule | WorkflowEstateRule
  >[] {
    return [
      this.workflowEstateRules,
      this.workflowJurisdictionRules,
      this.workflowBillingPlanRules,
      this.workflowPaymentStatusRules
    ].filter(
      (collection: BaseWorkflowRuleCollection<WorkflowJurisdictionRule | WorkflowEstateRule>) =>
        collection.hasItems
    );
  }

  get workflowRuleSelects(): WorkflowRuleSelect[] {
    return this.workflowRuleCollections.map(
      (ruleCollection: BaseWorkflowRuleCollection<WorkflowJurisdictionRule | WorkflowEstateRule>) =>
        WorkflowRuleSelect.mapModel({
          type: ruleCollection.ruleType,
          selection: ruleCollection.ruleSelection
        })
    );
  }

  syncWorkflowRuleSelects(ruleSelects: WorkflowRuleSelect[]): void {
    this.workflowRuleCollections.forEach(
      (
        ruleCollection: BaseWorkflowRuleCollection<
          WorkflowJurisdictionRule | WorkflowEstateRule | WorkflowBillingPlanRule
        >
      ) => {
        ruleCollection.clearRules(ruleSelects);
      }
    );

    ruleSelects.forEach((ruleSelect: WorkflowRuleSelect) => {
      switch (ruleSelect.type) {
        case WorkflowRuleType.PRODUCT.key:
          this.workflowEstateRules.sync(ruleSelect.selection as EstateType[]);
          break;
        case WorkflowRuleType.JURISDICTION.key:
          this.workflowJurisdictionRules.sync(ruleSelect.selection as Jurisdiction[]);
          break;
        case WorkflowRuleType.BILLING_PLAN.key:
          this.workflowBillingPlanRules.sync(ruleSelect.selection as BillingPlanMapping[]);
          break;
        case WorkflowRuleType.PAYMENT_STATUS.key:
          this.workflowPaymentStatusRules.sync(ruleSelect.selection as EstatePaymentStatus[]);
          break;
      }
    });
  }

  syncWorkflowTriggerSelect(triggerSelect: WorkflowTrigger): void {
    this.clearOldTriggers(triggerSelect.type);

    this.triggerType = triggerSelect.type;

    switch (triggerSelect.type) {
      case WorkflowTriggerType.ESTATE_STATUS_CHANGED.key:
      case WorkflowTriggerType.ESTATE_HEALTH_CHECK_RECOMMENDATION_STATUS_CHANGED.key:
        this.workflowEstateStatusTrigger = WorkflowEstateStatusTrigger.mapModel({
          ...this.workflowEstateStatusTrigger,
          status: triggerSelect.trigger as string
        });
        break;
      case WorkflowTriggerType.GUIDANCE_STEP_COMPLETED.key:
        this.workflowStepCompletedTrigger = WorkflowStepCompletedTrigger.mapModel({
          ...this.workflowStepCompletedTrigger,
          key: triggerSelect.trigger as number
        });
        break;
      case WorkflowTriggerType.NOTARY_STATUS_CHANGED.key:
        this.workflowNotaryStatusTrigger = WorkflowNotaryStatusTrigger.mapModel({
          ...this.workflowNotaryStatusTrigger,
          status: triggerSelect.trigger as string
        });
        break;
    }
  }

  initializeProfessionalAssignmentWorkflow(): void {
    this.triggerType = WorkflowTriggerType.ESTATE_CREATED.key;

    if (this.actionType) {
      return;
    }

    this.actionType = WorkflowActionType.BULK_ASSIGNMENT.key;
  }

  initializeEstateProfessionalAssignmentWorkflow(): void {
    this.triggerType = WorkflowTriggerType.NEEDS_ESTATE_PROFESSIONAL.key;
  }

  private clearOldTriggers(newTriggerType?: string): void {
    const toClear: string[] = WorkflowTriggerType.getValuesByProp('key')
      .filter((triggerType: string) => triggerType !== newTriggerType)
      .map((triggerType: string) => this.getRelationFromTriggerType(triggerType))
      .filter((relation: string | undefined) => !!relation) as string[];

    toClear.forEach((relation: string): void => {
      // @ts-ignore
      this[relation] = null;
    });
  }

  private getRelationFromTriggerType(triggerType?: string): string | undefined {
    switch (triggerType) {
      case WorkflowTriggerType.ESTATE_STATUS_CHANGED.key:
      case WorkflowTriggerType.ESTATE_HEALTH_CHECK_RECOMMENDATION_STATUS_CHANGED.key:
        return 'workflowEstateStatusTrigger';
      case WorkflowTriggerType.GUIDANCE_STEP_COMPLETED.key:
        return 'workflowStepCompletedTrigger';
      case WorkflowTriggerType.NOTARY_STATUS_CHANGED.key:
        return 'workflowNotaryStatusTrigger';
      default:
        return undefined;
    }
  }
}
